<template>
    <div>
        <div class="mb-5">
            <h3>Quote Stats</h3>
            <table class="table table-striped customer-highlight-table">
                <thead>
                    <tr>
                        <th v-for="header in headers['Quote Stats']" :key="header.label">
                            {{ header.label }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(row, idx) in quoteStats" :key="idx">
                        <tr v-if="row['Part Number']" :class="rowClass(row['Part Number'])">
                            <td>
                                <PartNumberDisplay
                                    :part-number="row['Part Number']"
                                    :selected-part="part_selected"
                                />
                            </td>
                            <td>{{ displayValue(getYearValue(row, prevYear)) }}</td>
                            <td>{{ displayValue(getYearValue(row, currentYear)) }}</td>
                            <td>{{ displayValue(row['Last Quote']) }}</td>
                            <td>{{ displayValue(row['Tot']) }}</td>
                            <td>{{ displayValue(row['Tot Qty']) }}</td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <div class="mb-5">
            <h3>Sale Stats</h3>
            <table class="table table-striped customer-highlight-table">
                <thead>
                    <tr>
                        <th v-for="header in headers['Sale Stats']" :key="header.label">
                            {{ header.label }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(row, idx) in saleStats" :key="idx">
                        <tr v-if="row['Part Number']" :class="rowClass(row['Part Number'])">
                            <td>
                                <PartNumberDisplay
                                    :part-number="row['Part Number']"
                                    :selected-part="part_selected"
                                />
                            </td>
                            <td>{{ displayValue(getYearValue(row, prevYear)) }}</td>
                            <td>{{ displayValue(getYearValue(row, currentYear)) }}</td>
                            <td>{{ displayValue(row['Last Sale']) }}</td>
                            <td>{{ displayValue(row['Tot']) }}</td>
                            <td>{{ displayValue(row['Tot Qty']) }}</td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <div class="mb-5">
            <h3>Margin</h3>
            <table class="table table-striped customer-highlight-table">
                <thead>
                    <tr>
                        <th v-for="header in headers['Margin']" :key="header.label">
                            {{ header.label }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(row, idx) in marginStats" :key="idx">
                        <tr v-if="row['part_number']" :class="rowClass(row['part_number'])">
                            <td>
                                <PartNumberDisplay
                                    :part-number="row['part_number']"
                                    :selected-part="part_selected"
                                />
                            </td>
                            <td>{{ displayMoney(row['AVG Price']) }}</td>
                            <td>{{ displayMoney(row['Avg Cost']) }}</td>
                            <td>{{ displayMoney(row['Avg Margin']) }}</td>
                            <td>{{ displayMoney(row['Avg Quote']) }}</td>
                            <td>{{ displayPercent(row['Won']) }}</td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
    import { mapState } from 'pinia'
    import { formatNumeric, normalizePart } from '@/helpers/string.js'
    import {
        buildDashboardRowHighlightClass,
        compareSelectedPartFirst,
    } from '@/helpers/dashboardRowHighlight.js'
    import PartNumberDisplay from '@/components/shared/PartNumberDisplay.vue'
    import { useSubstitutesStore } from '@/stores/substitutesStore'

    export default {
        name: 'StatsTable',
        components: {
            PartNumberDisplay,
        },
        props: {
            data: {
                type: Array,
                required: true,
            },
            part_selected: {
                type: String,
                default: '',
            },
            highlightedCustomer: {
                type: String,
                default: '',
            },
        },
        data() {
            const currentYear = new Date().getFullYear()
            const prevYear = currentYear - 1

            return {
                headers: {
                    'Quote Stats': [
                        { label: 'Part Number', type: 'string' },
                        { label: `${prevYear}`, type: 'numeric' },
                        { label: `${currentYear}`, type: 'numeric' },
                        { label: 'Last Quote', type: 'string' },
                        { label: 'Tot', type: 'numeric' },
                        { label: 'Tot Qty', type: 'numeric' },
                    ],
                    'Sale Stats': [
                        { label: 'Part Number', type: 'string' },
                        { label: `${prevYear}`, type: 'numeric' },
                        { label: `${currentYear}`, type: 'numeric' },
                        { label: 'Last Sale', type: 'string' },
                        { label: 'Tot', type: 'numeric' },
                        { label: 'Tot Qty', type: 'numeric' },
                    ],
                    Margin: [
                        { label: 'Part Number', type: 'string' },
                        { label: 'AVG Price', type: 'numeric' },
                        { label: 'AVG Cost', type: 'numeric' },
                        { label: 'AVG Margin', type: 'numeric' },
                        { label: 'AVG Quote', type: 'numeric' },
                        { label: 'Won', type: 'numeric' },
                    ],
                },
            }
        },
        computed: {
            ...mapState(useSubstitutesStore, ['selectedSubstituteOnModal']),
            currentYear() {
                return new Date().getFullYear()
            },
            prevYear() {
                return this.currentYear - 1
            },
            quoteStats() {
                const response = []
                this.data.statsData.map((element) => {
                    element[0].data.forEach((entry) => {
                        response.push(entry)
                    })
                })

                return this.sortSelectedPartRows(response, 'Part Number')
            },
            saleStats() {
                const response = []
                this.data.statsData.map((element) => {
                    element[1].data.forEach((entry) => {
                        response.push(entry)
                    })
                })

                return this.sortSelectedPartRows(response, 'Part Number')
            },
            marginStats() {
                const response = []
                this.data.statsData.map((element) => {
                    element[2].data.forEach((entry) => {
                        response.push({
                            ...entry,
                            part_number: element.part,
                        })
                    })
                })

                return this.sortSelectedPartRows(response, 'part_number')
            },
        },
        methods: {
            formatNumeric,
            sortSelectedPartRows(rows, partKey) {
                return rows.slice().sort((a, b) =>
                    compareSelectedPartFirst(a?.[partKey], b?.[partKey], this.part_selected)
                )
            },
            displayValue(value) {
                return value === null || value === undefined || value === '' ? 'N/A' : value
            },
            getYearValue(row, year) {
                if (!row || !year) return null
                const numericYear = String(year)
                const directValue = row[numericYear]
                if (directValue !== undefined) return directValue

                const yearKey = Object.keys(row).find((key) => key.startsWith(`${numericYear}-`))
                if (!yearKey) return null
                return row[yearKey]
            },
            displayMoney(value) {
                if (value === null || value === undefined || value === '') return 'N/A'
                return formatNumeric(value, 'money') || 'N/A'
            },
            displayPercent(value) {
                if (value === null || value === undefined || value === '') return 'N/A'
                const numericValue = Number(value)
                if (!Number.isFinite(numericValue)) return 'N/A'
                return `${Math.round(numericValue)} %`
            },
            isActivePart(partNumber) {
                if (!this.selectedSubstituteOnModal || partNumber == null) return false
                const selected = normalizePart(this.selectedSubstituteOnModal)
                const current = normalizePart(partNumber)
                return selected.length > 0 && selected === current
            },
            rowClass(partNumber) {
                return buildDashboardRowHighlightClass({
                    highlightedCustomer: this.highlightedCustomer,
                    selectedPart: this.part_selected,
                    partNumber,
                    activePart: this.isActivePart(partNumber),
                })
            },
        },
    }
</script>

<style>
    .table {
        margin-bottom: 1rem;
        width: 100%;
        border: 1px solid var(--color-border-muted);
    }
    .table th {
        background-color: var(--color-surface-subtle);
        text-align: left;
    }
    .table td,
    .table th {
        padding: 0.75rem;
        vertical-align: top;
        border-top: 1px solid var(--color-border-muted);
    }

    .table thead td,
    .table thead th {
        font-size: 15px;
        font-weight: 700;
        text-transform: uppercase;
    }
</style>
